
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Session } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { AuthService } from '../services/authService';
import { logWithTimestamp } from '../utils/authUtils';
import { useAuthSession } from '../hooks/useAuthSession';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isAuthenticated: boolean | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const initialAuthContext: AuthContextType = {
  session: null,
  user: null,
  isAuthenticated: null,
  isLoading: true,
  signOut: async () => {},
  refreshSession: async () => {},
};

const AuthContext = createContext<AuthContextType>(initialAuthContext);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();
  const { 
    session, 
    user, 
    isAuthenticated, 
    isLoading: isSessionLoading, 
    refreshSession 
  } = useAuthSession();
  const [isLoading, setIsLoading] = useState(true);
  
  // Monitor profile and handle user setup
  useEffect(() => {
    let isMounted = true;
    
    const setupUserProfile = async () => {
      if (user?.id && isAuthenticated && session?.access_token) {
        try {
          logWithTimestamp('Setting up user profile');
          await AuthService.ensureUserProfile(user.id, session.access_token);
        } catch (err) {
          logWithTimestamp('Error setting up user profile:', err);
        }
      }
    };

    if (user && isAuthenticated && !isSessionLoading) {
      setupUserProfile();
    }

    // Update loading state based on session loading
    if (isMounted && !isSessionLoading) {
      setIsLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [user, isAuthenticated, isSessionLoading, session]);

  const handleSignOut = async () => {
    try {
      logWithTimestamp("Sign out requested");
      setIsLoading(true);
      await AuthService.signOut();
      toast.success('Successfully signed out');
      navigate('/');
    } catch (error) {
      logWithTimestamp('Error signing out:', error);
      toast.error('Failed to sign out');
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    session,
    user,
    isAuthenticated,
    isLoading: isLoading || isSessionLoading,
    signOut: handleSignOut,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
